import { TFunction } from 'i18next';

import { HIGHLIGHT, HighLightConfigType } from '~/modules/highlight/constants';
import analytics from '~/services/analytics';

export const DETECTOR_MAX_FILE_SIZE = 20000000; //20MB
export const DETECTOR_VALID_FILE_EXTENSIONS = ['pdf', 'docx', 'txt'];

export enum DETECTION_STAGE {
  FULLY_HUMAN = 'fully_human',
  MOSTLY_HUMAN = 'mostly_human',
  MEDIUM = 'medium',
  MOSTLY_AI = 'mostly_ai',
  FULLY_AI = 'fully_ai',
}

export const IsShowedDetectorTutorial = 'is_showed_detector_tutorial_v2';
export const AiDetectorTitle = 'AI Content Detector';

export const DETECTION_EDGE: Record<DETECTION_STAGE, number> = {
  [DETECTION_STAGE.FULLY_AI]: 100,
  [DETECTION_STAGE.MOSTLY_AI]: 61,
  [DETECTION_STAGE.MEDIUM]: 31,
  [DETECTION_STAGE.MOSTLY_HUMAN]: 1,
  [DETECTION_STAGE.FULLY_HUMAN]: 0,
};

export const getAiDetectorHighlightConfig = (
  t: TFunction,
): Record<
  | HIGHLIGHT.AI_DETECTOR_HIGHLIGHTS_TITLE
  | HIGHLIGHT.AI_DETECTOR_HIGHLIGHTS_TEXT
  | HIGHLIGHT.AI_DETECTOR_HUMANIZE_TEXT
  | HIGHLIGHT.AI_DETECTOR_DETAILED_REPORT
  | HIGHLIGHT.AI_DETECTOR_TIPS_PANEL,
  HighLightConfigType
> => {
  return {
    [HIGHLIGHT.AI_DETECTOR_HIGHLIGHTS_TITLE]: {
      title: t(
        'highlight:ai_detector.sentences_highlights.title',
        'AI Sentences Highlights',
      ),
      text: t(
        'highlight:ai_detector.navigator.text_v2',
        'These buttons allow you to highlight the next or previous sentence that is probably AI-generated',
      ),
      placement: 'bottom',
      buttonText: t('general:next', 'Next'),
      onShowCallBack: (): void => {
        analytics.trackEvent('tutorial - show tip', {
          tool: AiDetectorTitle,
          tip_name: HIGHLIGHT.AI_DETECTOR_HIGHLIGHTS_TITLE,
        });
      },
      onSubmitCallback: (): void => {
        analytics.trackEvent('tutorial - click next', {
          tool: AiDetectorTitle,
          tip_name: HIGHLIGHT.AI_DETECTOR_HIGHLIGHTS_TITLE,
        });
      },
    },
    [HIGHLIGHT.AI_DETECTOR_HIGHLIGHTS_TEXT]: {
      title: t(
        'highlight:ai_detector.sentences_highlights.title',
        'AI Sentences Highlights',
      ),
      text: t(
        'highlight:ai_detector.sentences_highlights.text',
        'Selected AI-generated sentence is highlighted in yellow, the rest — in blue',
      ),
      placement: 'right',
      buttonText: t('general:next', 'Next'),
      onShowCallBack: (): void => {
        analytics.trackEvent('tutorial - show tip', {
          tool: AiDetectorTitle,
          tip_name: HIGHLIGHT.AI_DETECTOR_HIGHLIGHTS_TEXT,
        });
      },
      onSubmitCallback: (): void => {
        analytics.trackEvent('tutorial - click next', {
          tool: AiDetectorTitle,
          tip_name: HIGHLIGHT.AI_DETECTOR_HIGHLIGHTS_TEXT,
        });
      },
    },
    [HIGHLIGHT.AI_DETECTOR_HUMANIZE_TEXT]: {
      text: t(
        'highlight:ai_detector.humanize_button.text',
        'Use this feature to decrease the probability of the text being flagged by AI detection software',
      ),
      placement: 'bottom',
      buttonText: t('general:next', 'Next'),
      onShowCallBack: (): void => {
        analytics.trackEvent('tutorial - show tip', {
          tool: AiDetectorTitle,
          tip_name: HIGHLIGHT.AI_DETECTOR_HUMANIZE_TEXT,
        });
      },
      onSubmitCallback: (): void => {
        analytics.trackEvent('tutorial - click next', {
          tool: AiDetectorTitle,
          tip_name: HIGHLIGHT.AI_DETECTOR_HUMANIZE_TEXT,
        });
      },
    },
    [HIGHLIGHT.AI_DETECTOR_DETAILED_REPORT]: {
      text: t(
        'highlight:ai_detector.detailed_report_button.text',
        'Get a document with complete information about the text regarding AI content inside',
      ),
      placement: 'bottom',
      buttonText: t('general:next', 'Next'),
      onShowCallBack: (): void => {
        analytics.trackEvent('tutorial - show tip', {
          tool: AiDetectorTitle,
          tip_name: HIGHLIGHT.AI_DETECTOR_DETAILED_REPORT,
        });
      },
      onSubmitCallback: (): void => {
        analytics.trackEvent('tutorial - click next', {
          tool: AiDetectorTitle,
          tip_name: HIGHLIGHT.AI_DETECTOR_DETAILED_REPORT,
        });
      },
    },
    [HIGHLIGHT.AI_DETECTOR_TIPS_PANEL]: {
      text: t(
        'highlight:ai_detector.tips_button.text',
        'Try using our proven tips to eliminate AI traces when humanized text still gets flagged as AI-generated',
      ),
      placement: 'bottom',
      buttonText: t('general:finish', 'Finish'),
      onShowCallBack: (): void => {
        analytics.trackEvent('tutorial - show tip', {
          tool: AiDetectorTitle,
          tip_name: HIGHLIGHT.AI_DETECTOR_TIPS_PANEL,
        });
      },
      onSubmitCallback: (updateUserExtraFields): void => {
        updateUserExtraFields({
          highlights: { [IsShowedDetectorTutorial]: true },
        });
        analytics.trackEvent('tutorial - finished', {
          tool: AiDetectorTitle,
          tip_name: HIGHLIGHT.AI_DETECTOR_TIPS_PANEL,
        });
      },
    },
  };
};

export const AIDetectorMultilingualExperimentKey =
  'experiment-multilingual-ai-detector';
